import { parseDay, type Day } from "../dashboard/event-dates";

/**
 * Which events are over, and how a list of them splits into "live and
 * upcoming" and "Past".
 *
 * An event is over when its last calendar day has gone by IN THE EVENT'S OWN
 * ZONE, not the reader's: a conference in Auckland that ended on the 5th is
 * over for a reader in Berlin at the moment it is the 6th in Auckland, and not
 * when it turns the 6th in Berlin. The last day is the end date, or the start
 * date when there is no (readable) end date. An event with no readable start
 * date has no day to measure from and is never over.
 *
 * Every function takes the instant it is asked at, so none of them reads the
 * clock and all of them are testable at any moment.
 */

/** How many past events a list shows before "Show all". */
export const PAST_CAP = 5;

export interface EventTiming {
  startDate?: string | null;
  endDate?: string | null;
  /** IANA zone of the event. Absent on old membership cards. */
  timezone?: string | null;
  /** Absent means false. */
  archived?: boolean | null;
}

export function readerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const formatters = new Map<string, Intl.DateTimeFormat | null>();

function formatterFor(timeZone: string): Intl.DateTimeFormat | null {
  if (!formatters.has(timeZone)) {
    try {
      formatters.set(
        timeZone,
        new Intl.DateTimeFormat("en-US", {
          timeZone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
      );
    } catch {
      // Not an IANA zone this runtime knows.
      formatters.set(timeZone, null);
    }
  }
  return formatters.get(timeZone) ?? null;
}

/** The calendar day it is at `now` in `timeZone`, as `yyyy-mm-dd`. */
function dayIn(now: Date, timeZone: string): string | null {
  const formatter = formatterFor(timeZone);
  if (!formatter) return null;
  const parts = formatter.formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)?.value;
  const [year, month, day] = [part("year"), part("month"), part("day")];
  return year && month && day
    ? `${year.padStart(4, "0")}-${month}-${day}`
    : null;
}

const asString = (day: Day) =>
  `${String(day.year).padStart(4, "0")}-${String(day.month).padStart(2, "0")}-${String(day.day).padStart(2, "0")}`;

/**
 * The event's last calendar day, `yyyy-mm-dd`: the later of start and end, the
 * start alone when the end is absent or unreadable, null with no readable
 * start.
 */
export function lastDay(event: EventTiming): string | null {
  const start = parseDay(event.startDate);
  if (!start) return null;
  const startText = asString(start);
  const end = parseDay(event.endDate);
  const endText = end ? asString(end) : startText;
  return endText > startText ? endText : startText;
}

/**
 * Has the event's last day gone by in the event's own zone?
 *
 * The reader's zone is the fallback only when the event carries no zone (an
 * old membership card) or one this runtime cannot read, because that is the
 * only clock left to measure it on.
 */
export function eventHasEnded(
  event: EventTiming,
  now: Date,
  readerZone: string = readerTimeZone(),
): boolean {
  const last = lastDay(event);
  if (!last) return false;
  const zone = event.timezone?.trim() || readerZone;
  const today = dayIn(now, zone) ?? dayIn(now, readerZone);
  return today !== null && today > last;
}

export function isArchived(event: EventTiming): boolean {
  return event.archived === true;
}

/** Past is over, or archived: an archived event never reads as current. */
export function isPastEvent(
  event: EventTiming,
  now: Date,
  readerZone?: string,
): boolean {
  return isArchived(event) || eventHasEnded(event, now, readerZone);
}

/**
 * Newest first by last day, then by start day. Events with no readable dates
 * go last. Stable, so events that tie keep the order they came in.
 */
export function sortNewestFirst<T>(
  items: readonly T[],
  timing: (item: T) => EventTiming,
): T[] {
  const key = (item: T) => {
    const event = timing(item);
    const start = parseDay(event.startDate);
    return [lastDay(event) ?? "", start ? asString(start) : ""] as const;
  };
  return items
    .map((item, index) => ({ item, index, key: key(item) }))
    .sort(
      (a, b) =>
        (a.key[0] < b.key[0] ? 1 : a.key[0] > b.key[0] ? -1 : 0) ||
        (a.key[1] < b.key[1] ? 1 : a.key[1] > b.key[1] ? -1 : 0) ||
        a.index - b.index,
    )
    .map((row) => row.item);
}

export interface PastSplit<T> {
  /** Live, upcoming and undated, in the order given. */
  current: T[];
  /** The past events to show: all of them, or the newest PAST_CAP. */
  past: T[];
  /** Every past event, whether shown or not. */
  pastTotal: number;
  /** More past events than are shown, so "Show all" has something to do. */
  hasMorePast: boolean;
}

export function splitPast<T>(
  items: readonly T[],
  timing: (item: T) => EventTiming,
  now: Date,
  options: { showAll?: boolean; readerZone?: string } = {},
): PastSplit<T> {
  const readerZone = options.readerZone ?? readerTimeZone();
  const current: T[] = [];
  const past: T[] = [];
  for (const item of items) {
    (isPastEvent(timing(item), now, readerZone) ? past : current).push(item);
  }
  const newestFirst = sortNewestFirst(past, timing);
  const hasMorePast = newestFirst.length > PAST_CAP;
  return {
    current,
    past:
      options.showAll || !hasMorePast
        ? newestFirst
        : newestFirst.slice(0, PAST_CAP),
    pastTotal: newestFirst.length,
    hasMorePast,
  };
}

/** Archived events and the rest, for the lists that hide archived by default. */
export function splitArchived<T>(
  items: readonly T[],
  timing: (item: T) => EventTiming,
): { active: T[]; archived: T[] } {
  const active: T[] = [];
  const archived: T[] = [];
  for (const item of items) {
    (isArchived(timing(item)) ? archived : active).push(item);
  }
  return { active, archived: sortNewestFirst(archived, timing) };
}
